import { useState, useMemo, useCallback, useEffect } from 'react'
import { Chip, Stack, Typography, Select, MenuItem, FormControl } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { useIsMobile } from '@/hooks/Responsive'
import BoardList from '@components/Board/BoardList'
import BoardListTable from '@components/Board/BoardListTable'
import BoardListCard from '@components/Board/BoardListCard'
import { fetchMyManagementList, fetchMyManagementComboList } from '@/api/mypage'
import { useAuth } from '@/components/AuthContext'
import { useLang } from '@/i18n/useLang'
import { buildLangHref } from '@data/nav'

export function createManagementTableColumns({ isMobile, onTitleClick }) {
  const renderTitle = (v, row) => {
    const statusColor = {
      강의중: 'primary',
      대기중: 'secondary',
      종강: 'secondary2',
    }[row.lectureStatus] || 'grey'//LECTURE_STATUS

    return (
      <Stack direction={isMobile ? 'column' : 'row'} alignItems={isMobile ? 'flex-start' : 'center'} spacing={isMobile ? 2 : 1}>
        {row.lectureStatus && (
          <Chip
            label={row.lectureStatus}
            variant="outlined"
            color={statusColor}
          />
        )}
        <Typography
          component="span"
          role="link"
          tabIndex={0}
          variant={isMobile ? 'subtitle3' : 'body1'}
          sx={{
            cursor: 'pointer',
            overflow: isMobile ? 'visible' : 'hidden',
            textOverflow: isMobile ? 'unset' : 'ellipsis',
            whiteSpace: isMobile ? 'normal' : 'nowrap',
            wordBreak: 'keep-all',
            '&:hover': { textDecoration: 'underline', fontWeight: '700' },
          }}
          title={String(v ?? '')}
          onClick={() => onTitleClick?.(row)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') onTitleClick?.(row)
          }}
        >
          {v}
        </Typography>
      </Stack>
    )
  }

  const renderProgressRate = (v, row) => {
    return (
      <Typography color="text.secondary" fontWeight={500}>{v}%</Typography>
      )
  }

  return [
    { field: 'rowNumber', headerName: '번호', sortable: false, width: '70px', align: 'center', hideInCard: true },
    // { field: 'eduClassficName', headerName: '학과', sortable: false, width: '170px', align: 'center' },
    { field: 'eduTitle', headerName: '강좌명', sortable: false, width: 'auto', align: 'left', render: renderTitle, isTitle: true, hideTitleInCard: true },
    { field: 'eduDays', headerName: '수강기간', sortable: false, width: '230px', align: 'center' },
    { field: 'traineeCnt', headerName: '수강자', sortable: false, width: '70px', align: 'center' },
    { field: 'progressRatePer', headerName: '진도율', sortable: false, width: '70px', align: 'center', render: renderProgressRate },
  ]
}

export function ManagementToolbar({ isMobile, status, department, onStatusChange, onDepartmentChange, departments = [] }) {
  return (
    <Stack
      direction={isMobile ? 'column' : 'row'}
      alignItems="center"
      spacing={1}
      sx={{
        flex: 'auto',
        width: isMobile ? '100%' : 'auto',
      }}
    >
      {/* 학과 */}
      {/* <FormControl sx={{ flex: 'auto', width: isMobile ? '100%' : '200px' }}>
        <Select
          name="department"
          value={department}
          onChange={(e) => onDepartmentChange(e.target.value)}
          displayEmpty
          MenuProps={{
            PaperProps: {
              sx: {
                width: '100%',
                maxWidth: (theme) =>
                  isMobile ? `calc(100vw - ${theme.spacing(8)})` : '200px',
              },
            },
          }}
          sx={{
            width: '100%',
            maxWidth: (theme) =>
              isMobile ? `calc(100vw - ${theme.spacing(8)})` : '200px',
          }}
        >
          <MenuItem value="전체학과">전체학과</MenuItem>
          {departments.map((item) => (
            <MenuItem key={item.code} value={item.code}>
              {item.codeNm}
            </MenuItem>
          ))}
        </Select>
      </FormControl> */}

      {/* 상태 */}
      <FormControl sx={{ flex: 'auto', width: isMobile ? '100%' : '160px' }}>
        <Select
          name="status"
          value={status}
          onChange={(e) => onStatusChange(e.target.value)}
          displayEmpty
          MenuProps={{
            PaperProps: {
              sx: {
                width: '100%',
                maxWidth: (theme) =>
                  isMobile ? `calc(100vw - ${theme.spacing(8)})` : '160px',
              },
            },
          }}
          sx={{
            width: '100%',
            maxWidth: (theme) =>
              isMobile ? `calc(100vw - ${theme.spacing(8)})` : '160px',
          }}
        >
          <MenuItem value="전체">전체</MenuItem>
          <MenuItem value="강의중">강의중</MenuItem>
          <MenuItem value="대기중">대기중</MenuItem>
          <MenuItem value="종강">종강</MenuItem>
        </Select>
      </FormControl>
    </Stack>
  )
}

export default function MyManagement() {
  const navigate = useNavigate()
  const { isMobile } = useIsMobile()
  const lang = useLang()
  const { user, loading, isAuthReady } = useAuth();

  const [departments, setDepartments] = useState([]);
  const [statusFilter, setStatusFilter] = useState('전체') 
  const [departmentFilter, setDepartmentFilter] = useState('전체학과')
  const [confirmedStatus, setConfirmedStatus] = useState('전체') 
  const [confirmedDepartment, setConfirmedDepartment] = useState('전체학과') 
  const [confirmedSearch, setConfirmedSearch] = useState('')

  useEffect(() => {
    const getDepartments = async () => {
      try {
        const response = await fetchMyManagementComboList({ params: { lecturerAccountId: user.accountId } });
        setDepartments(response.data || []);
      } catch (error) {
        console.error("Failed to fetch departments:", error);
      }
    };
    getDepartments();
  }, []);

  const handleDepartmentChange = useCallback((value) => {
    setDepartmentFilter(value)
  }, [])

  const handleStatusChange = useCallback((value) => {
    setStatusFilter(value)
  }, [])

  // fetcher
  const fetcher = useCallback(
    async ({ page, pageSize, sort }) => {
      const params = {
        page: page,
        size: pageSize,
        sort: sort ? `${sort.field},${sort.direction}` : 'no,desc',
        eduLectureId: confirmedDepartment !== '전체학과' ? confirmedDepartment : undefined,
        lectureStatus: confirmedStatus !== '전체' ? confirmedStatus : undefined,
        keyword: confirmedSearch || undefined,
        lecturerAccountId: user.accountId
      };

      Object.keys(params).forEach(key => params[key] === undefined && delete params[key]);

      try {
        const response = await fetchMyManagementList({ params });
        const { content, totalElements, totalPages } = response.data;
        return { items: content, total: totalElements, totalPages };
      } catch (error) {
        console.error("Failed to fetch management list:", error);
        return { items: [], total: 0, totalPages: 1 };
      }
    },
    [confirmedDepartment, confirmedStatus, confirmedSearch] 
  )

  const columns = useMemo(
    () =>
      createManagementTableColumns({
        isMobile,
        onTitleClick: (row) => navigate(buildLangHref(`/mypage/management/${row.eduLectureId}`, lang)),
      }),
    [isMobile, navigate]
  )

  const handleSearchSubmit = useCallback(
    (searchValue) => {
      setConfirmedStatus(statusFilter);
      setConfirmedDepartment(departmentFilter);
      setConfirmedSearch(searchValue);
    },
    [statusFilter, departmentFilter]
  );

  const toolbar = useMemo(
    () => (
      <ManagementToolbar
        isMobile={isMobile}
        status={statusFilter}
        department={departmentFilter}
        onStatusChange={handleStatusChange}
        onDepartmentChange={handleDepartmentChange}
        departments={departments}
      />
    ),
    [isMobile, statusFilter, departmentFilter, handleStatusChange, handleDepartmentChange, departments]
  );

  return (
    <>
      <BoardList
        view={isMobile ? 'card' : 'table'}
        columns={columns}
        fetcher={fetcher}
        TableComponent={BoardListTable}
        CardComponent={(props) => (
          <BoardListCard {...props} variant="row" isMobile={isMobile} />
        )}
        initialSort={{ field: 'no', direction: 'desc' }}
        initialPage={1}
        initialPageSize={10}
        toolbarLeft={toolbar}
        onSearchSubmit={handleSearchSubmit}
      />
    </>
  );
}

